import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Global } from '@services/core/Global';
import { Observable } from 'rxjs';

export function periodKey(p: { year?: number; period?: number } | null | undefined): number {
  if (!p || p.year == null || p.period == null) return 0;
  return Number(p.year) * 100 + Number(p.period);
}

export function periodLabel(p: { year?: number; period?: number } | null | undefined): string {
  if (!p?.year || p.period == null) return '';
  return `${String(p.period).padStart(2, '0')}-${p.year}`;
}

@Injectable({
  providedIn: 'root'
})
export class PeriodService {
  private http = inject(HttpClient);
  private url = Global.url + 'V1/period';
  private dashUrl = Global.url + 'V1/dashboard';
  private mailUrl = Global.url + 'V1/mail';

  readonly periods = signal<any[]>([]);
  readonly loading = signal(false);
  /** Sube después de recargar la lista (login, cron, cierre). Las primas recargan cobros. */
  readonly revision = signal(0);

  readonly activePeriod = computed(() => {
    const list = this.periods()
      .filter(p => p.statusSlug === 'ACTIVE')
      .sort((a, b) => periodKey(b) - periodKey(a));
    return list[0] ?? null;
  });

  readonly activeLabel = computed(() => periodLabel(this.activePeriod()));

  readonly billingPeriods = computed(() =>
    this.periods()
      .filter(p => p.statusSlug !== 'PENDING_ENTITY' && (!p.statusRef || p.statusRef.category === 'ENTITY'))
      .sort((a, b) => periodKey(b) - periodKey(a))
      .map(p => ({
        ...p,
        displayName: `${p.year} - ${String(p.period).padStart(2, '0')}`
      }))
  );

  /** Periodos cerrados listos para contabilizar ASINET (más reciente primero). */
  readonly closedBillingPeriods = computed(() =>
    this.billingPeriods().filter((p) => String(p.statusSlug || '').toUpperCase() === 'INACTIVE')
  );

  /** Id del periodo cerrado más reciente, o null si no hay. */
  latestClosedPeriodId(): number | null {
    return this.closedBillingPeriods()[0]?.id ?? null;
  }

  /**
   * Rango contable por defecto: periodo 01 del año del último cerrado → último cerrado.
   * Ej.: cerrado mayo 2026 → 01-2026 … 05-2026. Si no hay cerrados, null.
   */
  defaultClosedYearRangeIds(): { startId: number; endId: number } | null {
    const closed = this.closedBillingPeriods();
    if (!closed.length) return null;
    const end = closed[0];
    const year = Number(end.year);
    const yearPeriods = this.billingPeriods()
      .filter((p) => Number(p.year) === year)
      .sort((a, b) => periodKey(a) - periodKey(b));
    const start = yearPeriods.find((p) => Number(p.period) === 1) ?? yearPeriods[0] ?? end;
    const startKey = periodKey(start);
    const endKey = periodKey(end);
    if (startKey > endKey) return { startId: end.id, endId: end.id };
    return { startId: start.id, endId: end.id };
  }

  reload(): void {
    this.loading.set(true);
    this.get().subscribe({
      next: (res) => {
        this.periods.set(res || []);
        this.loading.set(false);
        this.revision.update(n => n + 1);
      },
      error: () => this.loading.set(false)
    });
  }

  get(filters: any = {}): Observable<any[]> {
    return this.http.get<any[]>(this.url, { params: filters });
  }

  getYears(): Observable<any[]> {
    return this.http.get<any[]>(this.url + '/years');
  }

  create(data: any): Observable<any> {
    return this.http.post<any>(this.url, data);
  }

  createBatch(year: number): Observable<any> {
    return this.http.post<any>(this.url + '/batch', { year });
  }

  update(id: number, data: any): Observable<any> {
    return this.http.put<any>(`${this.url}/${id}`, data);
  }

  getSummaryByEntity(periodId: number): Observable<any[]> {
    return this.http.get<any[]>(this.dashUrl + '/summary-by-entity', { params: { periodId } });
  }

  bulkNotify(periodId: number, sendNotifications: boolean): Observable<any> {
    return this.http.post<any>(this.url + '/bulk-notify', { periodId, sendNotifications });
  }

  openPeriod(periodId: number): Observable<any> {
    return this.http.post<any>(this.url + '/open-period', { periodId });
  }

  openNextPeriod(): Observable<any> {
    return this.http.post<any>(this.url + '/open-next', {});
  }

  closePeriod(id: number): Observable<any> {
    return this.http.post<any>(this.url + '/close-period', { id });
  }

  sendMail(mailOptions: any): Observable<any> {
    return this.http.post<any>(this.mailUrl + '/send-mail', mailOptions);
  }
}
